import axios from 'axios';
import { 
  EVALUATION
} from '@/helpers/evaluation/evaluation-endpoints.js';

// New API endpoints following the instructions
const API_BASE = EVALUATION;

const endpoints = {
  // Procurement Process & Committee
  getProcurementProcess: (id) => `${API_BASE}/procurement-process/${id}`,
  getCommittee: (procurementId) => `${API_BASE}/committee/procurement/${procurementId}`,
  getCodeOfConductStatus: (committeeId) => `${API_BASE}/committee/${committeeId}/conduct-status`,
  getConflictStatus: (committeeId) => `${API_BASE}/committee/${committeeId}/conflict-status`,
  
  // Bid Management
  getBids: (procurementId) => `${API_BASE}/bids/procurement/${procurementId}`,
  
  // Evaluation Process
  startEvaluation: `${API_BASE}/start`,
  getEvaluationSections: (procurementId) => `${API_BASE}/sections/${procurementId}`,
  
  // Stage-specific Evaluations
  preliminaryEvaluation: `${API_BASE}/preliminary/evaluate`,
  preliminarySummary: (evaluationId) => `${API_BASE}/preliminary/summary/${evaluationId}`,
  
  technicalEvaluation: `${API_BASE}/technical/evaluate`,
  technicalConsensus: `${API_BASE}/technical/consensus`,
  getTechnicalConsensus: (evaluationId) => `${API_BASE}/technical/consensus/${evaluationId}`,
  getIndividualTechnicalScores: (evaluationId, bidId) => `${API_BASE}/technical/individual-scores/${evaluationId}/${bidId}`,
  
  commercialEvaluation: `${API_BASE}/commercial/evaluate`,
  
  financialEvaluation: `${API_BASE}/financial/evaluate`,
  
  // Ranking & Results
  generateRanking: `${API_BASE}/ranking/generate`,
  
  // Post-qualification
  postQualification: `${API_BASE}/post-qualification`,
  
  // Award Decision
  createAwardDecision: `${API_BASE}/award/decision`,
  approveAwardDecision: (awardDecisionId) => `${API_BASE}/award/decision/${awardDecisionId}/approve`,
  sendAwardNotifications: (awardDecisionId) => `${API_BASE}/award/decision/${awardDecisionId}/notify`,
  completeStandstillPeriod: (awardDecisionId) => `${API_BASE}/award/decision/${awardDecisionId}/complete-standstill`,
  finalizeContractAward: (awardDecisionId) => `${API_BASE}/award/decision/${awardDecisionId}/finalize`,
  
  // Shortlisting (Expression of Interest)
  createShortlist: `${API_BASE}/award/shortlist`,
  getShortlist: (procurementId) => `${API_BASE}/award/shortlist/${procurementId}`,
  approveShortlist: (shortlistId) => `${API_BASE}/award/shortlist/${shortlistId}/approve`,
  publishShortlist: (shortlistId) => `${API_BASE}/award/shortlist/${shortlistId}/publish`,
  
  // Appeals
  submitAppeal: `${API_BASE}/appeals/submit`,
  getAppeals: (procurementId) => `${API_BASE}/appeals/procurement/${procurementId}`,
};

export const state = {
  // Current Evaluation Context
  currentProcurementProcess: null,
  currentCommittee: null,
  currentEvaluation: null,
  currentBids: [],
  currentSections: [],
  
  // Evaluation Progress
  evaluationStages: [
    { name: 'PRELIMINARY', label: 'Preliminary Evaluation', order: 1, completed: false },
    { name: 'TECHNICAL', label: 'Technical Evaluation', order: 2, completed: false },
    { name: 'COMMERCIAL', label: 'Commercial Evaluation', order: 3, completed: false },
    { name: 'FINANCIAL', label: 'Financial Evaluation', order: 4, completed: false },
    { name: 'POST_QUALIFICATION', label: 'Post Qualification', order: 5, completed: false }
  ],
  currentStage: 'PRELIMINARY',
  
  // Individual Evaluations (by stage and bid)
  evaluations: {
    preliminary: {},
    technical: {},
    commercial: {},
    financial: {}
  },
  
  // Auto-save state
  autoSaveEnabled: true,
  lastSaved: null,
  hasUnsavedChanges: false,
  
  // Consensus & Collaboration
  consensusIssues: [],
  individualScores: {},
  consensusResolutions: {},
  
  // Results & Rankings
  finalRanking: [],
  eliminatedBids: [],
  recommendedBidder: null,
  
  // Award/Shortlist Decision
  awardDecision: null,
  shortlist: null,
  
  // UI State
  loading: {
    procurement: false,
    committee: false,
    bids: false,
    sections: false,
    evaluation: false,
    consensus: false,
    ranking: false,
    award: false
  },
  
  errors: {
    procurement: null,
    committee: null,
    bids: null,
    sections: null,
    evaluation: null,
    consensus: null,
    ranking: null,
    award: null
  }
};

export const mutations = {
  // Procurement Process
  SET_PROCUREMENT_PROCESS(state, process) {
    state.currentProcurementProcess = process;
    // Update stages based on procurement method and configuration
    if (process) {
      state.evaluationStages = state.evaluationStages.map(stage => ({
        ...stage,
        applicable: isStageApplicable(stage.name, process)
      }));
    }
  },
  
  SET_COMMITTEE(state, committee) {
    state.currentCommittee = committee;
  },
  
  SET_BIDS(state, bids) {
    state.currentBids = bids;
  },
  
  SET_SECTIONS(state, sections) {
    state.currentSections = sections;
  },
  
  // Evaluation Progress
  SET_CURRENT_STAGE(state, stage) {
    state.currentStage = stage;
  },
  
  UPDATE_STAGE_COMPLETION(state, { stage, completed }) {
    const stageIndex = state.evaluationStages.findIndex(s => s.name === stage);
    if (stageIndex !== -1) {
      state.evaluationStages[stageIndex].completed = completed;
    }
  },
  
  // Individual Evaluations
  SET_EVALUATION(state, { stage, bidId, evaluation }) {
    if (!state.evaluations[stage]) {
      state.evaluations[stage] = {};
    }
    state.evaluations[stage][bidId] = evaluation;
    state.hasUnsavedChanges = true;
  },
  
  UPDATE_EVALUATION_FIELD(state, { stage, bidId, field, value }) {
    if (!state.evaluations[stage]) {
      state.evaluations[stage] = {};
    }
    if (!state.evaluations[stage][bidId]) {
      state.evaluations[stage][bidId] = {};
    }
    state.evaluations[stage][bidId][field] = value;
    state.hasUnsavedChanges = true;
  },
  
  // Auto-save
  SET_AUTO_SAVE_STATUS(state, { enabled, lastSaved = null, hasUnsavedChanges = null }) {
    state.autoSaveEnabled = enabled;
    if (lastSaved !== null) state.lastSaved = lastSaved;
    if (hasUnsavedChanges !== null) state.hasUnsavedChanges = hasUnsavedChanges;
  },
  
  // Consensus
  SET_CONSENSUS_ISSUES(state, issues) {
    state.consensusIssues = issues;
  },
  
  SET_INDIVIDUAL_SCORES(state, { evaluationId, bidId, scores }) {
    if (!state.individualScores[evaluationId]) {
      state.individualScores[evaluationId] = {};
    }
    state.individualScores[evaluationId][bidId] = scores;
  },
  
  SET_CONSENSUS_RESOLUTION(state, { bidId, resolution }) {
    state.consensusResolutions[bidId] = resolution;
  },
  
  // Results
  SET_FINAL_RANKING(state, ranking) {
    state.finalRanking = ranking.ranking || [];
    state.eliminatedBids = ranking.eliminatedBids || [];
    state.recommendedBidder = ranking.recommendedBidder || null;
  },
  
  SET_AWARD_DECISION(state, decision) {
    state.awardDecision = decision;
  },
  
  SET_SHORTLIST(state, shortlist) {
    state.shortlist = shortlist;
  },
  
  // Loading States
  SET_LOADING(state, { key, value }) {
    state.loading[key] = value;
  },
  
  // Error States
  SET_ERROR(state, { key, error }) {
    state.errors[key] = error;
  },
  
  // Clear all evaluation data (for new procurement)
  CLEAR_EVALUATION_DATA(state) {
    state.currentProcurementProcess = null;
    state.currentCommittee = null;
    state.currentEvaluation = null;
    state.currentBids = [];
    state.currentSections = [];
    state.currentStage = 'PRELIMINARY';
    state.evaluations = { preliminary: {}, technical: {}, commercial: {}, financial: {} };
    state.consensusIssues = [];
    state.individualScores = {};
    state.consensusResolutions = {};
    state.finalRanking = [];
    state.eliminatedBids = [];
    state.recommendedBidder = null;
    state.awardDecision = null;
    state.shortlist = null;
    state.hasUnsavedChanges = false;
    state.lastSaved = null;
    
    // Reset stage completion
    state.evaluationStages = state.evaluationStages.map(stage => ({
      ...stage,
      completed: false
    }));
  }
};

export const actions = {
  // Initialize evaluation workspace
  async initializeEvaluation({ commit, dispatch }, procurementId) {
    try {
      commit('CLEAR_EVALUATION_DATA');
      
      // Load procurement process details
      await dispatch('fetchProcurementProcess', procurementId);
      
      // Load committee details
      await dispatch('fetchCommittee', procurementId);
      
      // Load bids for evaluation
      await dispatch('fetchBids', procurementId);
      
      // Load evaluation sections/criteria
      await dispatch('fetchEvaluationSections', procurementId);
      
      return { success: true };
    } catch (error) {
      console.error('Failed to initialize evaluation:', error);
      return { success: false, error };
    }
  },
  
  // Fetch procurement process details
  async fetchProcurementProcess({ commit }, procurementId) {
    commit('SET_LOADING', { key: 'procurement', value: true });
    commit('SET_ERROR', { key: 'procurement', error: null });
    
    try {
      const response = await axios.get(endpoints.getProcurementProcess(procurementId));
      
      if (response.data.responseCode === 'SUCCESS') {
        commit('SET_PROCUREMENT_PROCESS', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.responseMessage || 'Failed to fetch procurement process');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.responseMessage || error.message;
      commit('SET_ERROR', { key: 'procurement', error: errorMessage });
      throw error;
    } finally {
      commit('SET_LOADING', { key: 'procurement', value: false });
    }
  },
  
  // Fetch committee details
  async fetchCommittee({ commit }, procurementId) {
    commit('SET_LOADING', { key: 'committee', value: true });
    commit('SET_ERROR', { key: 'committee', error: null });
    
    try {
      const response = await axios.get(endpoints.getCommittee(procurementId));
      
      if (response.data.responseCode === 'SUCCESS') {
        commit('SET_COMMITTEE', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.responseMessage || 'Failed to fetch committee');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.responseMessage || error.message;
      commit('SET_ERROR', { key: 'committee', error: errorMessage });
      throw error;
    } finally {
      commit('SET_LOADING', { key: 'committee', value: false });
    }
  },
  
  // Fetch bids for evaluation
  async fetchBids({ commit }, procurementId) {
    commit('SET_LOADING', { key: 'bids', value: true });
    commit('SET_ERROR', { key: 'bids', error: null });
    
    try {
      const response = await axios.get(endpoints.getBids(procurementId));
      
      if (response.data.responseCode === 'SUCCESS') {
        commit('SET_BIDS', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.responseMessage || 'Failed to fetch bids');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.responseMessage || error.message;
      commit('SET_ERROR', { key: 'bids', error: errorMessage });
      throw error;
    } finally {
      commit('SET_LOADING', { key: 'bids', value: false });
    }
  },
  
  // Fetch evaluation sections/criteria
  async fetchEvaluationSections({ commit }, procurementId) {
    commit('SET_LOADING', { key: 'sections', value: true });
    commit('SET_ERROR', { key: 'sections', error: null });
    
    try {
      const response = await axios.get(endpoints.getEvaluationSections(procurementId));
      
      if (response.data.responseCode === 'SUCCESS') {
        commit('SET_SECTIONS', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.responseMessage || 'Failed to fetch evaluation sections');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.responseMessage || error.message;
      commit('SET_ERROR', { key: 'sections', error: errorMessage });
      throw error;
    } finally {
      commit('SET_LOADING', { key: 'sections', value: false });
    }
  },
  
  // Start evaluation process
  async startEvaluation({ commit, state }, { evaluationStartDate, initiatedBy, initiatedByName }) {
    commit('SET_LOADING', { key: 'evaluation', value: true });
    commit('SET_ERROR', { key: 'evaluation', error: null });
    
    try {
      const payload = {
        procurementProcessId: state.currentProcurementProcess.id,
        committeeId: state.currentCommittee.id,
        evaluationStartDate,
        initiatedBy,
        initiatedByName
      };
      
      const response = await axios.post(endpoints.startEvaluation, payload);
      
      if (response.data.responseCode === 'SUCCESS') {
        // Update current evaluation context
        commit('SET_CURRENT_STAGE', response.data.data.currentStage || 'PRELIMINARY_EVALUATION');
        return response.data.data;
      } else {
        throw new Error(response.data.responseMessage || 'Failed to start evaluation');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.responseMessage || error.message;
      commit('SET_ERROR', { key: 'evaluation', error: errorMessage });
      throw error;
    } finally {
      commit('SET_LOADING', { key: 'evaluation', value: false });
    }
  },
  
  // Submit preliminary evaluation
  async submitPreliminaryEvaluation({ commit, state }, evaluationData) {
    console.log(state)
    commit('SET_LOADING', { key: 'evaluation', value: true });
    
    try {
      const response = await axios.post(endpoints.preliminaryEvaluation, evaluationData);
      
      if (response.data.responseCode === 'SUCCESS') {
        // Update local state
        commit('SET_EVALUATION', {
          stage: 'preliminary',
          bidId: evaluationData.bidId,
          evaluation: evaluationData
        });
        
        commit('SET_AUTO_SAVE_STATUS', { 
          lastSaved: new Date().toISOString(), 
          hasUnsavedChanges: false 
        });
        
        return response.data;
      } else {
        throw new Error(response.data.responseMessage || 'Failed to submit preliminary evaluation');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.responseMessage || error.message;
      commit('SET_ERROR', { key: 'evaluation', error: errorMessage });
      throw error;
    } finally {
      commit('SET_LOADING', { key: 'evaluation', value: false });
    }
  },
  
  // Auto-save evaluation (debounced)
  async autoSaveEvaluation({ commit, state }, { stage, evaluationData }) {
    if (!state.autoSaveEnabled) return;
    
    try {
      // This would be called by a debounced function in components
      const endpoint = getEvaluationEndpointForStage(stage);
      const response = await axios.post(endpoint, evaluationData);
      
      if (response.data.responseCode === 'SUCCESS') {
        commit('SET_AUTO_SAVE_STATUS', { 
          lastSaved: new Date().toISOString(), 
          hasUnsavedChanges: false 
        });
      }
    } catch (error) {
      console.warn('Auto-save failed:', error);
      // Don't throw error for auto-save failures
    }
  }
};

export const getters = {
  // Current evaluation context
  currentProcurement: state => state.currentProcurementProcess,
  currentCommittee: state => state.currentCommittee,
  currentBids: state => state.currentBids,
  currentSections: state => state.currentSections,
  
  // Evaluation progress
  currentStage: state => state.currentStage,
  evaluationProgress: state => {
    const total = state.evaluationStages.filter(s => s.applicable !== false).length;
    const completed = state.evaluationStages.filter(s => s.completed && s.applicable !== false).length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  },
  
  // Stage-specific getters
  preliminaryEvaluations: state => state.evaluations.preliminary,
  technicalEvaluations: state => state.evaluations.technical,
  commercialEvaluations: state => state.evaluations.commercial,
  financialEvaluations: state => state.evaluations.financial,
  
  // Get evaluation for specific bid and stage
  getEvaluationForBid: state => (bidId, stage) => {
    return state.evaluations[stage] ? state.evaluations[stage][bidId] : null;
  },
  
  // ZPPA compliance checks
  isZPPACompliant: state => {
    const process = state.currentProcurementProcess;
    if (!process) return false;
    
    return (
      process.minimumTechnicalScore >= 70 && // ZPPA minimum technical threshold
      (process.technicalScoreWeight + process.commercialScoreWeight + process.financialScoreWeight) === 100 // Weights sum to 100%
    );
  },
  
  // Check if ready for next stage
  canProceedToNextStage: state => {
    // Implementation would check completion status of current stage
    const currentStageData = state.evaluationStages.find(s => s.name === state.currentStage);
    return currentStageData ? currentStageData.completed : false;
  },
  
  // Consensus requirements
  hasConsensusIssues: state => state.consensusIssues.length > 0,
  
  // Results and rankings
  finalResults: state => ({
    ranking: state.finalRanking,
    eliminated: state.eliminatedBids,
    recommended: state.recommendedBidder
  }),
  
  // Determine outcome type based on procurement method
  shouldCreateShortlist: state => {
    return state.currentProcurementProcess?.procurementMethod === 'EXPRESSION_OF_INTEREST';
  },
  
  shouldCreateAward: state => {
    const method = state.currentProcurementProcess?.procurementMethod;
    return ['OPEN_TENDER', 'RESTRICTED_TENDER', 'SINGLE_SOURCE', 'EMERGENCY'].includes(method);
  },
  
  // UI state
  isLoading: state => key => state.loading[key] || false,
  getError: state => key => state.errors[key],
  hasUnsavedChanges: state => state.hasUnsavedChanges
};

// Helper functions
function isStageApplicable(stageName, procurementProcess) {
  switch (stageName) {
    case 'COMMERCIAL':
      return procurementProcess.hasCommercialSection;
    case 'POST_QUALIFICATION':
      return procurementProcess.requiresPostQualification;
    default:
      return true;
  }
}

function getEvaluationEndpointForStage(stage) {
  switch (stage) {
    case 'preliminary': return endpoints.preliminaryEvaluation;
    case 'technical': return endpoints.technicalEvaluation;
    case 'commercial': return endpoints.commercialEvaluation;
    case 'financial': return endpoints.financialEvaluation;
    default: throw new Error(`Unknown evaluation stage: ${stage}`);
  }
}